import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, Mail, ArrowLeft, CheckCircle, MessageSquare, Brain, Workflow, BarChart3, ExternalLink, Shield } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/utils';


const features = [
  {
    icon: Phone,
    title: 'Sistema PBX VoIP',
    description: 'Centralino cloud completo con IVR, code, gruppi e voicemail',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    icon: MessageSquare,
    title: 'CRM WhatsApp & SMS',
    description: 'Campagne multicanale con WhatsApp Business e SMS integrati',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  {
    icon: Brain,
    title: 'AI Voice Agent',
    description: 'Agenti vocali AI con trascrizione real-time e sentiment analysis',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  {
    icon: Workflow,
    title: 'Workflows Automation',
    description: 'Automazioni visive drag-drop per marketing e CRM',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    icon: BarChart3,
    title: 'Analytics Real Time',
    description: 'Dashboard live con metriche chiamate, performance e KPI',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
];

export default function ForgotPassword() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`/api/${tenantSlug}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        toast({
          title: "Email inviata",
          description: "Controlla la tua casella di posta per le istruzioni.",
        });
      } else {
        throw new Error(data.error || 'Errore durante la richiesta');
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: getErrorMessage(error) || "Si è verificato un errore. Riprova.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col lg:flex-row min-h-screen">

        <div className="lg:w-1/2 flex flex-col justify-center p-8 lg:p-12 xl:p-16">
          <div className="mb-8 lg:mb-12">
            <div className="flex items-center gap-3 mb-4">
              <img src="/logos/easyflow-full-v3.svg" alt="EasyFlow" className="h-[67px]" />
            </div>
            <p className="text-lg text-muted-foreground">
              La piattaforma AI-powered per la gestione completa delle comunicazioni aziendali
            </p>
          </div>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${feature.iconBg} flex items-center justify-center`}>
                  <feature.icon className={`h-6 w-6 ${feature.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-8">
                <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full 
                              bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg mb-4">
                  {isSubmitted ? (
                    <CheckCircle className="h-8 w-8 text-white" />
                  ) : (
                    <Shield className="h-8 w-8 text-white" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {isSubmitted ? 'Email Inviata' : 'Password Dimenticata'}
                </h2>
                <p className="text-sm text-white/80 mt-1">
                  {isSubmitted 
                    ? 'Controlla la tua casella di posta' 
                    : 'Inserisci la tua email per ricevere il link di reset'}
                </p>
              </div>

              {isSubmitted ? (
                <div className="space-y-5">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                    <p className="text-white/90 text-sm text-center">
                      Se l'indirizzo <strong className="text-white">{email}</strong> è registrato nel sistema, 
                      riceverai un'email con le istruzioni per reimpostare la password.
                    </p>
                    <p className="text-white/60 text-xs text-center mt-3">
                      Il link scadrà tra 30 minuti.
                    </p>
                  </div>
                  <Link to={`/${tenantSlug}/login`}>
                    <Button
                      className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 
                                hover:scale-[1.02] transition-all duration-200 shadow-lg"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Torna al Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-white">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          className="bg-white/90 border-white/20 pl-10 focus-visible:ring-white/50
                                    text-gray-900 placeholder:text-gray-500 transition-all duration-200"
                          placeholder="Inserisci la tua email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 
                                hover:scale-[1.02] transition-all duration-200 shadow-lg"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Invio in corso...' : 'Invia Link di Reset'}
                    </Button>
                  </form>

                  <div className="mt-4 text-center">
                    <Link 
                      to={`/${tenantSlug}/login`}
                      className="text-sm text-white/90 hover:text-white transition-colors font-medium hover:underline"
                    >
                      <ArrowLeft className="inline-block mr-1 h-4 w-4" />
                      Torna al Login
                    </Link>
                  </div>
                </>
              )}

              <div className="mt-8 pt-6 border-t border-white/20 text-center">
                <span className="text-xs text-white/60">
                  crafted by{' '}
                  <a
                    href="https://www.easydigitalgroup.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors inline-flex items-center gap-0.5 hover:underline"
                  >
                    easydigitalgroup
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
